import React from 'react';
import { UpcomingCustomInterview } from '../../api/roundsApi';
import './InterviewDetailsModal.css';

interface InterviewDetailsModalProps {
  interviews: UpcomingCustomInterview[] | null;
  onClose: () => void;
}

const InterviewDetailsModal: React.FC<InterviewDetailsModalProps> = ({ interviews, onClose }) => {
  if (!interviews) return null;

  return (
    <div className="interview-details-backdrop" onClick={onClose}>
      <div className="interview-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="interview-details-header">
          <h2>Interview Details</h2>
          <button className="interview-details-close" onClick={onClose}>✕</button>
        </div>
        <div className="interview-details-body">
          {interviews.map((ci) => (
            <div key={ci.id} className="interview-details-card">
              <div className="interview-details-card-header">
                <div className="info-cell name-cell">
                  <span className="info-cell-label">Interview</span>
                  <h3>{ci.name}</h3>
                </div>
                <div className="info-cell deadline-cell">
                  <span className="info-cell-label">Deadline</span>
                  <span className="info-cell-value">
                    {new Date(ci.deadline).toLocaleDateString('en-US', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="info-cell badges-cell">
                  <span className={`interview-details-badge ${ci.accepted ? 'accepted' : 'pending'}`}>
                    {ci.accepted ? 'Accepted' : 'Not Accepted'}
                  </span>
                  <span
                    className={`interview-details-badge ${
                      ci.jdProcessingStatus === 'processed' ? 'processed' : 'processing'
                    }`}
                  >
                    {ci.jdProcessingStatus === 'processed' ? 'JD Processed' : 'JD Processing'}
                  </span>
                </div>
              </div>

              {ci.jdExtractedData ? (
                <>
                  <div className="jd-role-row">
                    <span className="jd-role-name">{ci.jdExtractedData.role_name || 'Role'}</span>
                    {(ci.jdExtractedData.company_name || ci.jdExtractedData.domain_name) && (
                      <span className="jd-role-sub">
                        {ci.jdExtractedData.company_name && <>at {ci.jdExtractedData.company_name}</>}
                        {ci.jdExtractedData.company_name && ci.jdExtractedData.domain_name && ' — '}
                        {ci.jdExtractedData.domain_name}
                      </span>
                    )}
                  </div>

                  <div className="jd-grid-row">
                    <div className="jd-grid-col">
                      <h4>Skills</h4>
                      {ci.jdExtractedData.skills && ci.jdExtractedData.skills.length > 0 ? (
                        <div className="jd-skill-list">
                          {ci.jdExtractedData.skills.map((s, i) => (
                            <span key={i} className="jd-skill">
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="jd-empty">No skills listed</p>
                      )}
                    </div>
                    <div className="jd-grid-col">
                      <h4>Qualifications</h4>
                      {ci.jdExtractedData.qualifications && ci.jdExtractedData.qualifications.length > 0 ? (
                        <ul className="jd-list">
                          {ci.jdExtractedData.qualifications.map((q, i) => (
                            <li key={i}>{q}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="jd-empty">No qualifications listed</p>
                      )}
                    </div>
                  </div>

                  <div className="jd-grid-row">
                    <div className="jd-grid-col">
                      <h4>Responsibilities</h4>
                      {ci.jdExtractedData.responsibilities && ci.jdExtractedData.responsibilities.length > 0 ? (
                        <ul className="jd-list">
                          {ci.jdExtractedData.responsibilities.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="jd-empty">No responsibilities listed</p>
                      )}
                    </div>
                    <div className="jd-grid-col">
                      <h4>Additional Info</h4>
                      {(() => {
                        const addInfo = ci.jdExtractedData?.additional_info || {};
                        const hasAddInfo = Object.values(addInfo).some((v) => v && v.trim());
                        return hasAddInfo ? (
                          <div className="jd-kv-list">
                            {Object.entries(addInfo).map(([k, v]) =>
                              v && v.trim() ? (
                                <div key={k} className="jd-kv">
                                  <span className="jd-kv-label">
                                    {k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
                                  </span>
                                  <span className="jd-kv-value">{v}</span>
                                </div>
                              ) : null
                            )}
                          </div>
                        ) : (
                          <p className="jd-empty">None</p>
                        );
                      })()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="interview-details-section">
                  <h4>Job Description</h4>
                  <p className="interview-details-jd">{ci.jobDescription || 'No job description provided.'}</p>
                </div>
              )}

              <div className="interview-details-section">
                <h4>Rounds ({ci.rounds.length})</h4>
                {ci.rounds.length > 0 ? (
                  <div className="interview-details-rounds">
                    {ci.rounds.map((round) => {
                      const roundStatus = round.status || 'pending';
                      return (
                        <div key={round.round_number} className="interview-details-round">
                          <div className="interview-details-round-header">
                            <span className="interview-details-round-num">Round {round.round_number}</span>
                            <span className="interview-details-round-type">
                              {round.type === 'aptitude'
                                ? 'Mock Interview'
                                : round.type === 'coding'
                                ? 'Coding Challenge'
                                : round.type === 'gd'
                                ? 'Group Discussion'
                                : round.type}
                            </span>
                            <span className={`interview-details-round-status ${roundStatus}`}>{roundStatus}</span>
                          </div>
                          <div className="interview-details-round-details">
                            {round.scheduledDate && (
                              <span className="interview-details-detail">
                                <span className="detail-label">Scheduled</span>
                                {new Date(round.scheduledDate).toLocaleDateString('en-US', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })}
                              </span>
                            )}
                            {round.config &&
                              Object.entries(round.config).map(([key, val]) =>
                                val != null && val !== '' ? (
                                  <span key={key} className="interview-details-detail">
                                    <span className="detail-label">
                                      {key
                                        .replace(/([A-Z])/g, ' $1')
                                        .replace(/_/g, ' ')
                                        .replace(/^\w/, (c) => c.toUpperCase())}
                                      :
                                    </span>{' '}
                                    {String(val)}
                                    {key === 'duration' ? ' min' : ''}
                                  </span>
                                ) : null
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="interview-details-no-rounds">No rounds configured yet.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InterviewDetailsModal;
